import { useContext } from 'react';
import { useQuery } from 'react-query';
import { ServerErrorResponse, baseApiUrl, fetcher } from 'data_sources/constants';
import { AppContext } from 'views/App/context';
import { PolicyBarChartResponse } from '../_types';
import { generatePolicyTrends } from './riskPoolOrgDataGenerator';
import { useRiskPoolDemoData } from '../../../../_data_sources/useRiskPoolDemoData';

interface QueryParams {
  startDate: string;
  endDate: string;
  manualId?: string;
}

export const getPolicyTrends = async (
  parentOrgId: string,
  childOrgId: string,
  queryParams: QueryParams,
  signal?: AbortSignal,
  onError?: (errors: ServerErrorResponse) => void
): Promise<PolicyBarChartResponse> => {
  const url = `${baseApiUrl()}/activity-center/v1/organizations/${parentOrgId}/risk-pool/related-organizations/${childOrgId}/policy/policy-trends`;

  const params = new URLSearchParams();
  if (queryParams.startDate) params.append('startDate', queryParams.startDate);
  if (queryParams.endDate) params.append('endDate', queryParams.endDate);

  const queryString = params.toString();
  const fullUrl = queryString ? `${url}?${queryString}` : url;

  try {
    const data: PolicyBarChartResponse = await fetcher({
      method: 'get',
      url: fullUrl,
      options: { signal },
    });

    return data;
  } catch (error: unknown) {
    if (onError && typeof error === 'object' && error !== null && 'errorDetails' in error) {
      onError(error as ServerErrorResponse);
    }
    throw error;
  }
};

export default function usePolicyTrends(
  childOrgId: string,
  queryParams: QueryParams,
  options: object = {},
  onError?: (errors: ServerErrorResponse) => void
) {
  const { currentOrganizationId } = useContext(AppContext);
  const parentOrgId = currentOrganizationId || childOrgId; // Fallback to childOrgId if not available
  const isDemoDataEnabled = useRiskPoolDemoData();

  return useQuery<PolicyBarChartResponse, Error>(
    [
      'risk_pool_org_policy_trends',
      parentOrgId,
      childOrgId,
      queryParams.startDate,
      queryParams.endDate,
      queryParams.manualId,
    ],
    async ({ signal }): Promise<PolicyBarChartResponse> => {
      try {
        if (isDemoDataEnabled) {
          return generatePolicyTrends(childOrgId, queryParams.startDate, queryParams.endDate);
        }

        return await getPolicyTrends(parentOrgId, childOrgId, queryParams, signal, onError);
      } catch (err) {
        if (onError) onError(err as ServerErrorResponse);
        throw err;
      }
    },
    {
      ...options,
      enabled: !!childOrgId && !!queryParams.startDate && !!queryParams.endDate,
    }
  );
}
